import { useCallback, useEffect, useState } from 'react'
import {
  deletedNoticeLines,
  deletedNoticeStorage,
  type DeletedNotice as Deleted,
} from 'lib/community/discussions-writer'
import styles from './DiscussionThread.module.css'

/**
 * The post the author just deleted, taken once after mount from what their Delete left in
 * sessionStorage (`deletedNoticeStorage`) — the list HTML is shared-cached and never carries it.
 * `deleted` stays for the whole visit (the page hides that card with it, since a cached list can
 * still hold it); `shown` is the note, until dismissed.
 */
export function useDeletedNotice(): {
  deleted: Deleted | null
  shown: boolean
  dismiss: () => void
} {
  const [deleted, setDeleted] = useState<Deleted | null>(null)
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const notice = deletedNoticeStorage.take()
    // Only a hit sets state: a second run (StrictMode) finds the key gone and must not clear it.
    if (notice) {
      setDeleted(notice)
      setShown(true)
    }
  }, [])
  const dismiss = useCallback(() => setShown(false), [])
  return { deleted, shown, dismiss }
}

/**
 * A one-time status note after the author's own delete — bold head, then what it means —
 * dismissible. Shared by the Discussions list (`DeletedNotice`) and the product hub (a deleted
 * owner review).
 */
export const DeleteNoticeBox: React.VFC<{
  head: string
  detail: string
  onDismiss: () => void
}> = ({ head, detail, onDismiss }) => (
  <div className={styles.postedNotice} role="status">
    <p className={styles.noticeText}>
      <strong>{head}</strong> {detail}
    </p>
    <button
      type="button"
      className={styles.postedNoticeClose}
      aria-label="Dismiss"
      onClick={onDismiss}
    >
      <span aria-hidden="true">{'✕'}</span>
    </button>
  </div>
)

/**
 * The author's one-time note on the Discussions list after Delete in the thread's "⋯" menu: what
 * happened ("Your question was deleted.") and what it means (`deletedNoticeLines`). A status
 * region, dismissible; null renders nothing.
 */
const DeletedNotice: React.VFC<{ notice: Deleted | null; onDismiss: () => void }> = ({
  notice,
  onDismiss,
}) => {
  if (!notice) {
    return null
  }
  const { head, detail } = deletedNoticeLines(notice)
  return <DeleteNoticeBox head={head} detail={detail} onDismiss={onDismiss} />
}

export default DeletedNotice
